import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { PackageCheck, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { POStatusBadge } from "@/components/POStatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Empty } from "@/components/Empty";
import { formatDate } from "@/lib/format";
import { WAREHOUSE_LABEL, WAREHOUSES } from "@/lib/constants";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const RECEIVE_STATUSES = ["approved", "ordered", "production_planned", "in_production",
                          "ready", "shipped", "in_transit"];

export default function Leveringen() {
  const { id } = useParams();
  const nav = useNavigate();
  const [pos, setPos] = useState([]);
  const [po, setPo] = useState(null);
  const [lines, setLines] = useState([]);
  const [warehouse, setWarehouse] = useState("vandenbrink");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (id) {
      api.get(`/purchase-orders/${id}`).then((r) => {
        setPo(r.data);
        setWarehouse(r.data.receive_warehouse || "vandenbrink");
        setLines(r.data.lines.map((l) => ({
          product_id: l.product_id, product_name: l.product_name,
          ordered_quantity: l.quantity, unit: l.unit,
          received_quantity: l.quantity, batch_number: "",
          expiry_date: new Date(Date.now() + 365 * 86400000).toISOString().slice(0, 10),
        })));
      });
    } else {
      api.get("/purchase-orders").then((r) => {
        setPos(r.data.filter((p) => RECEIVE_STATUSES.includes(p.status)));
      });
    }
  }, [id]);

  const submit = async () => {
    if (lines.some((l) => !l.batch_number || !l.expiry_date)) {
      toast.error("Elke regel vereist een batchnummer en THT-datum.");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/purchase-orders/${po.id}/receive`, {
        warehouse,
        lines: lines.map((l) => ({
          product_id: l.product_id,
          received_quantity: Number(l.received_quantity),
          batch_number: l.batch_number,
          expiry_date: l.expiry_date,
        })),
      });
      toast.success(`Levering ontvangen in ${WAREHOUSE_LABEL[warehouse]}. Voorraad automatisch bijgewerkt.`);
      nav(`/bestellingen/${po.id}`);
    } catch (e) {
      toast.error("Ontvangst mislukt", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  if (!id) {
    return (
      <div className="px-4 md:px-8 lg:px-12 py-8 md:py-10" data-testid="deliveries-list">
        <PageHeader eyebrow="Magazijn" title="Leveringen"
          description="Open een lopende bestelling om de levering te registreren." />
        {pos.length === 0 ? (
          <Empty icon={PackageCheck} title="Geen leveringen om te ontvangen"
            description="Zodra bestellingen op status Besteld → Onderweg staan, verschijnen ze hier." />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pos.map((p) => (
              <Link key={p.id} to={`/leveringen/${p.id}`} className="surface p-6 hover:border-primary/40 transition-colors"
                data-testid={`delivery-po-${p.po_number}`}>
                <div className="flex items-center justify-between">
                  <div className="font-mono text-lg font-semibold">{p.po_number}</div>
                  <POStatusBadge status={p.status} />
                </div>
                <div className="mt-2 text-base text-muted-foreground">{p.supplier_name}</div>
                <div className="mt-4 text-sm text-muted-foreground">
                  Verwacht {formatDate(p.expected_delivery_date)} · {p.lines?.length || 0} regels
                </div>
                <div className="mt-2 text-xs">→ {WAREHOUSE_LABEL[p.receive_warehouse]}</div>
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (!po) return <div className="px-8 py-12">Laden…</div>;

  return (
    <div className="px-4 md:px-8 lg:px-12 py-8 md:py-10" data-testid="delivery-form">
      <Link to="/leveringen" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="w-4 h-4" /> Terug
      </Link>
      <PageHeader eyebrow={`Ontvangst · ${po.supplier_name}`} title={po.po_number}
        description="Vul aantal, batchnummer en THT in. De voorraad wordt direct bijgewerkt." />

      <div className="surface p-6 mb-4">
        <Label className="text-base">Ontvangst in magazijn</Label>
        <Select value={warehouse} onValueChange={setWarehouse}>
          <SelectTrigger className="h-12 mt-2 max-w-md" data-testid="receive-warehouse-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {WAREHOUSES.map((w) => (
              <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-4">
        {lines.map((l, idx) => (
          <div key={l.product_id} className="surface p-6" data-testid={`receipt-line-${idx}`}>
            <div className="flex flex-col lg:flex-row gap-4 lg:items-end">
              <div className="flex-1 min-w-0">
                <div className="label-eyebrow">Product</div>
                <div className="font-semibold text-lg mt-1">{l.product_name}</div>
                <div className="text-sm text-muted-foreground">Besteld: {l.ordered_quantity} {l.unit}</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 w-full lg:w-auto lg:min-w-[640px]">
                <div>
                  <Label className="text-sm">Aantal ontvangen</Label>
                  <Input type="number" step="0.1" value={l.received_quantity}
                    onChange={(e) => setLines((ls) => ls.map((x, i) => i === idx ? { ...x, received_quantity: e.target.value } : x))}
                    className="h-16 mt-2 text-2xl font-mono" data-testid={`receipt-qty-${idx}`} />
                </div>
                <div>
                  <Label className="text-sm">Batchnummer</Label>
                  <Input value={l.batch_number}
                    onChange={(e) => setLines((ls) => ls.map((x, i) => i === idx ? { ...x, batch_number: e.target.value } : x))}
                    placeholder="B2026-XXXX"
                    className="h-16 mt-2 text-xl font-mono uppercase" data-testid={`receipt-batch-${idx}`} />
                </div>
                <div>
                  <Label className="text-sm">Houdbaar tot</Label>
                  <Input type="date" value={l.expiry_date}
                    onChange={(e) => setLines((ls) => ls.map((x, i) => i === idx ? { ...x, expiry_date: e.target.value } : x))}
                    className="h-16 mt-2 text-xl font-mono" data-testid={`receipt-expiry-${idx}`} />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="sticky bottom-4 md:bottom-6 mt-8 surface p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-lg">
        <div>
          <div className="label-eyebrow">Klaar om te bevestigen?</div>
          <div className="text-base mt-1">Voorraad wordt direct toegevoegd aan <span className="font-medium">{WAREHOUSE_LABEL[warehouse]}</span>. Bestelling gaat naar <span className="font-mono">ONTVANGEN</span>.</div>
        </div>
        <Button data-testid="confirm-receipt" onClick={submit} disabled={saving} className="touch-tap text-lg px-8">
          <PackageCheck className="w-5 h-5 mr-2" /> {saving ? "Bevestigen…" : "Ontvangst bevestigen"}
        </Button>
      </div>
    </div>
  );
}
